import { defineStore } from "pinia";
import axios, { AxiosResponse } from "axios";
import { computed, reactive, Ref, ref } from "vue";
import { Nullable } from "vitest";
import { useSpaceStore } from "./spaceStore";
import { useRouter } from "vue-router";

import * as Sentry from "@sentry/vue";
import { useTreeStore } from "./treeStore";
import { useMainStore } from "./mainStore";
import { useSocketStore } from "./socketStore";
import { useAxiosStore } from "./axiosStore";

type User = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
  created_at: string;
  impersonating?: boolean;
};

export const useAuthStore = defineStore("authStore", () => {
  // TODO should be removed for real live system
  const superUser = ["admin@admin.dev", "moeller.imoran@gmail.com"];

  const mainStore = useMainStore();
  const spaceStore = useSpaceStore();
  const axiosStore = useAxiosStore();
  const router = useRouter();
  const socketStore = useSocketStore();

  const user: Ref<Nullable<User>> = ref(null);
  const loggedIn: Ref<Boolean> = ref(false);

  const isSuperUser = computed(() => {
    return superUser.includes(user.value.email);
  });

  const globalLoading = ref(false);

  async function validateToken() {
    globalLoading.value = true;
    return new Promise((resolve, reject) =>
      axios
        .post("/auth/validateToken")
        .then((response) => {
          console.log(response);
          if (response.data.user) {
            loggedIn.value = true;
            user.value = response.data.user;

            socketStore.socket.emit("user", user.value.id);

            Sentry.setUser({
              id: user.value.id,
              email: user.value.email,
              username: user.value.first_name + " " + user.value.last_name
            });

            mainStore
              .loadCalculationSpaces()
              .then(() => {
                mainStore.loadOrganizations();
                router.replace(localStorage.getItem("restoreURL") || "/");
                resolve(true);
              })
              .catch(() => {
                reject();
              })
              .finally(() => {
                globalLoading.value = false;
              });
          } else {
            throw new Error("Invalid token");
          }
        })
        .catch((error) => {
          console.log(error);
          // user.value.impersonating = false;
          router.replace("/login");
          reject();
          globalLoading.value = false;
        })
    );
  }

  async function login(email: string, password: string, remember: boolean) {
    return new Promise((resolve, reject) => {
      axios
        .post("/auth/login", {
          email: email,
          password: password,
          remember: remember
        })
        .then((response) => {
          handleLogin(response, resolve, reject);
        })
        .catch((error) => {
          // Sentry.captureException(error);
          // Sentry.captureMessage(axios.defaults.baseURL);
          reject(error.response.data);
        });
    });
  }

  function handleLogin(response: AxiosResponse<any, any>, resolve, reject) {
    if (!response.data) {
      Sentry.captureMessage("Something went wrong on login");
    }

    if (response.data.user) {
      globalLoading.value = true;
      user.value = response.data.user;
      loggedIn.value = true;

      socketStore.socket.emit("user", user.value.id);

      Sentry.setUser({
        id: user.value.id,
        email: user.value.email,
        username: user.value.first_name + " " + user.value.last_name
      });

      mainStore.loadCalculationSpaces().then(() => {
        mainStore.loadOrganizations();
        router.replace(localStorage.getItem("restoreURL") || "/");
        resolve(true);
        globalLoading.value = false;
      });
      // router.replace({ name: "base-data" });
    }
  }

  async function signup(
    email: string,
    password: string,
    repeatPassword: string
  ) {
    return new Promise((resolve, reject) => {
      axios
        .post("/auth/signup", {
          email,
          password,
          repeatPassword
        })
        .then((response) => {
          if (!response.data) {
            Sentry.captureMessage("Unknown error");
            return reject("unknown-error");
          }
          router.push({ name: "thank-you", params: { action: "signup" } });
          resolve(true);
        })
        .catch((error) => {
          Sentry.captureException(error);
          reject(error.response.data);
        });
    });
  }

  async function logout() {
    // spaceStore delete all elements
    if (axiosStore.savingInBackground) {
      const result = confirm(
        "You have unsaved changes. Please wait for them to finish before logging out."
      );
      if (!result) {
        return;
      }
    }

    mainStore.activeSpaceId &&
      (await spaceStore.checkIfEverythingIsCorrectlyStored());

    loggedIn.value = false;
    document.cookie =
      "verificationCookie=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

    await router.push({ name: "login" });

    user.value = null;
    Sentry.setUser(null);
    spaceStore.clearData();
  }

  async function activate(token: string) {
    return axios.patch("/auth/activate", { token });
  }

  async function requestResetPassword(email: string) {
    return axios.patch("/auth/request-reset-password", { email: email });
  }

  async function setPassword(
    token: string,
    password: string,
    repeatPassword: string
  ) {
    return axios.patch("/auth/set-password", {
      token,
      password,
      repeatPassword
    });
  }

  async function impersonate(email: string) {
    if (email && superUser.includes(user.value.email)) {
      globalLoading.value = true;
      return axios
        .post("/auth/impersonate", {
          email
        })
        .then((_) => {
          window.location.reload();
        })
        .catch((error) => {
          // TODO Message invalid email
          console.log(error);
        })
        .finally(() => {
          globalLoading.value = false;
        });
    }
  }

  async function logoutImpersonate() {
    if (superUser.includes(user.value.email) && user.value.impersonating) {
      return axios
        .post("/auth/logout-impersonate")
        .then((response) => {
          document.cookie =
            "impersonate=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
          window.location.reload();
        })
        .finally(() => {});
    }
  }

  async function deleteAccount() {
    axios
      .post("/auth/delete-account")
      .then(async (response) => {
        console.log("Delete sussessfull");
        loggedIn.value = false;
        document.cookie =
          "verificationCookie=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        window.location.reload(true);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  async function changeUserName(first_name: string, last_name: string) {
    axios
      .post("/auth/change", {
        name: first_name,
        surname: last_name
      })
      .then(() => {
        console.log("successfully changed");
        user.value.first_name = first_name;
        user.value.last_name = last_name;
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.log("Error changing name", error);
      });
  }

  async function changePassword(
    oldPassword: string,
    newPassword: string,
    confirmPassword: string
  ) {
    return axios
      .post("/auth/change-password", {
        oldPassword,
        newPassword,
        confirmPassword
      })
      .then(() => {
        console.log("successfully changed");
      })
      .catch((error) => {
        console.log("Error changing password", error);
        throw error;
      });
  }

  async function createAccount(password, token, data) {
    return new Promise((resolve, reject) => {
      axios
        .post("/auth/create-account", { password, token, data })
        .then((response) => {
          handleLogin(response, resolve, reject);
        });
    });
  }

  return {
    superUser,
    deleteAccount,
    changeUserName,
    login,
    isSuperUser,
    signup,
    logout,
    validateToken,
    activate,
    setPassword,
    requestResetPassword,
    impersonate,
    logoutImpersonate,
    user,
    loggedIn,
    globalLoading,
    createAccount,
    changePassword
  };
});
